import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { NewsCategoryRequest } from './news-category.model';
import { NewsCategoryService } from './news-category.service';

@Component({
  selector: 'app-news-category',
  standalone: false,
  templateUrl: './news-category.component.html',
  styleUrls: ['./news-category.component.css']
})
export class NewsCategoryComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];
  categories: any[] = [];
  filteredCategories: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;
  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  newsCategoryId = 0;
  isEnabled = true;

  categoryNameKh = '';
  categoryNameEn = '';

  constructor(
    private newsCategoryService: NewsCategoryService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadCategories('');
  }

  loadLanguages(): void {
    this.languageService.getAll().subscribe({
      next: (res: any) => {
        this.languages = (res.data || []).sort((a: any, b: any) => a.languageId - b.languageId);

        if (this.languages.length > 0) {
          this.setTab(this.languages[0].code);
          this.setTabPopup(this.languages[0].languageId);
        }
      },
      error: () => this.showError('Failed to load languages.')
    });
  }

  loadCategories(langCode: string): void {
    this.newsCategoryService.getAll(langCode).subscribe({
      next: (res: any) => {
        const apiData: any[] = res.data || [];

        this.categories = apiData
          .filter(item => item.translations?.length)
          .flatMap(item =>
            item.translations.map((translation: any) => ({
              newsCategoryId: item.newsCategoryId,
              isEnabled: item.isEnabled,
              languageId: translation.languageId,
              languageCode: translation.languageCode,
              categoryName: translation.categoryName
            }))
          );

        this.filterCategories(this.activeTab || 'kh');
      },
      error: () => this.showError('Failed to load news categories.')
    });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterCategories(tab);
  }

  setTabPopup(tab: number): void {
    this.activeTabPopup = tab;
  }

  filterCategories(tab: string): void {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredCategories = this.categories.filter(category => {
      const status = category.isEnabled ? 'Active' : 'Inactive';
      const matchesTab = category.languageCode === tab;
      const matchesSearch = !keyword ||
        `${category.newsCategoryId ?? ''} ${category.categoryName ?? ''} ${status} ${category.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(lang: string): number {
    return this.categories.filter(category => category.languageCode === lang).length;
  }

  get pagedCategories(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredCategories.slice(start, start + this.pageSize);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredCategories.length / this.pageSize));
  }

  changePage(page: number): void {
    this.currentPage = page;
  }

  visibleButtonControl(eventName: string): void {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') {
      this.isEnableSave = true;
    } else if (eventName === 'Edit') {
      this.isEnableUpdate = true;
    } else if (eventName === 'Delete') {
      this.isEnableDelete = true;
    }
  }

  openModal(eventName?: string): void {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }

    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  addNew(): void {
    this.resetForm();
    this.openModal('Add');
  }

  viewData(id: number, eventName: 'Edit' | 'Delete'): void {
    this.newsCategoryId = id;

    this.newsCategoryService.getById(id, '').subscribe({
      next: (res: any) => {
        if (res.status !== 'success' || !res.data) {
          this.showError(res.message || 'News category not found.');
          return;
        }

        const data = res.data;
        this.newsCategoryId = data.newsCategoryId;
        this.isEnabled = data.isEnabled;
        this.categoryNameKh = '';
        this.categoryNameEn = '';

        data.translations.forEach((item: any) => {
          if (item.languageCode === 'kh') {
            this.categoryNameKh = item.categoryName || '';
          } else if (item.languageCode === 'en') {
            this.categoryNameEn = item.categoryName || '';
          }
        });

        const currentLanguage = this.languages.find(lang => lang.code === this.activeTab);
        this.activeTabPopup = currentLanguage?.languageId || this.languages[0]?.languageId || 1;
        this.openModal(eventName);
      },
      error: () => this.showError('Failed to load news category.')
    });
  }

  resetForm(): void {
    this.newsCategoryId = 0;
    this.isEnabled = true;
    this.categoryNameKh = '';
    this.categoryNameEn = '';
  }

  getRequest(): NewsCategoryRequest {
    return {
      isEnabled: this.isEnabled,
      translations: [
        {
          languageId: 1,
          categoryName: this.categoryNameKh ?? ''
        },
        {
          languageId: 2,
          categoryName: this.categoryNameEn ?? ''
        }
      ]
    };
  }

  save(): void {
    if (!this.validateForm()) {
      return;
    }

    this.clearMsg();
    this.newsCategoryService.create(this.getRequest()).subscribe({
      next: (res: any) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  update(): void {
    if (!this.validateForm()) {
      return;
    }

    this.clearMsg();
    this.newsCategoryService.update(this.newsCategoryId, this.getRequest()).subscribe({
      next: (res: any) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  delete(): void {
    if (!confirm('Are you sure you want to delete this news category?')) {
      return;
    }

    this.newsCategoryService.delete(this.newsCategoryId).subscribe({
      next: (res: any) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  refreshData(status: string, message: string): void {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadCategories('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  exceptionData(err: any): void {
    this.errorMessage = err?.error?.message || err?.error || 'Failed to complete news category request.';

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  private validateForm(): boolean {
    if (!this.categoryNameKh.trim() || !this.categoryNameEn.trim()) {
      this.showError('Please enter both Khmer and English category names.');
      return false;
    }

    return true;
  }

  private showError(message: string): void {
    this.errorMessage = message;

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }
}
